import React, {useEffect, useRef, useState} from 'react';
import {Lock} from 'lucide-react';

interface ModuleDemoOverlayProps {
    children: React.ReactNode;
    moduleName: string;
}

/**
 * Renders the module normally, then positions a blur + lock overlay on top of
 * the first card-like element (rounded border container), leaving the section
 * title fully readable.
 */
const ModuleDemoOverlay: React.FC<ModuleDemoOverlayProps> = ({children, moduleName}) => {
    const wrapperRef = useRef<HTMLDivElement>(null);
    const overlayRef = useRef<HTMLDivElement>(null);
    const [ready, setReady] = useState(false);

    useEffect(() => {
        const wrapper = wrapperRef.current;
        const overlay = overlayRef.current;
        if (!wrapper || !overlay) return;

        // Find the first card-like container inside the rendered module.
        const card =
            wrapper.querySelector<HTMLElement>('[class*="rounded-2xl"][class*="border"]') ??
            wrapper.querySelector<HTMLElement>('[class*="rounded-lg"][class*="border"]') ??
            wrapper.querySelector<HTMLElement>('[class*="rounded-2xl"]') ??
            wrapper.querySelector<HTMLElement>('table')?.closest<HTMLElement>('div[class*="rounded"]');

        if (!card) {
            // Fallback: cover the whole module minus the first ~50px (title area).
            overlay.style.top = '50px';
            overlay.style.left = '0';
            overlay.style.width = '100%';
            overlay.style.height = `${wrapper.scrollHeight - 50}px`;
            setReady(true);
            return;
        }

        const wrapperRect = wrapper.getBoundingClientRect();
        const cardRect = card.getBoundingClientRect();

        overlay.style.top = `${cardRect.top - wrapperRect.top}px`;
        overlay.style.left = `${cardRect.left - wrapperRect.left}px`;
        overlay.style.width = `${cardRect.width}px`;
        overlay.style.height = `${cardRect.height}px`;
        overlay.style.borderRadius = window.getComputedStyle(card).borderRadius;
        setReady(true);
    }, [children]);

    return (
        <div ref={wrapperRef} className="relative">
            <div className="pointer-events-none select-none">{children}</div>
            <div
                ref={overlayRef}
                className="absolute flex items-center justify-center overflow-hidden"
                style={{
                    backdropFilter: 'blur(5px)',
                    WebkitBackdropFilter: 'blur(5px)',
                    backgroundColor: 'rgba(255,255,255,0.3)',
                    opacity: ready ? 1 : 0,
                }}>
                <div className="flex flex-col items-center gap-2 text-gray-700">
                    <Lock className="h-8 w-8" />
                    <p className="text-center text-sm font-semibold">Contact us to unlock {moduleName}</p>
                </div>
            </div>
        </div>
    );
};

export default ModuleDemoOverlay;
